import React, { createContext, useState, useContext, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { getSupabaseClient } from '@/api/supabaseClient';
import { parseAuthCallbackUrl } from '@/lib/authRecovery';
import { toast } from 'sonner';

const AuthContext = createContext();

const mapSessionUser = (authUser) => {
  if (!authUser) return null;

  const fallbackName = authUser.email ? authUser.email.split('@')[0] : 'Usuario';
  return {
    id: authUser.id,
    email: authUser.email || '',
    full_name: authUser.user_metadata?.full_name || authUser.user_metadata?.name || fallbackName,
    role: authUser.app_metadata?.role || 'member',
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isLoadingPublicSettings, setIsLoadingPublicSettings] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [appPublicSettings, setAppPublicSettings] = useState(null); // Contains only { id, public_settings }

  useEffect(() => {
    checkAppState();
  }, []);

  useEffect(() => {
    let subscription;
    let isMounted = true;

    const handleAuthCallbackEvent = (event) => {
      const detail = event?.detail || {};
      if (detail.success === false && isMounted) {
        setAuthError({
          type: 'oauth_error',
          message: detail.error || 'Nao foi possivel concluir o login social.',
        });
        setIsLoadingAuth(false);
      }
    };

    const applyAuthUser = (authUser) => {
      if (!isMounted) return;

      const mappedUser = mapSessionUser(authUser);
      setUser(mappedUser);
      setIsAuthenticated(Boolean(mappedUser));
      setAuthError(null);
      setIsLoadingAuth(false);
    };

    const completeWebAuthCallbackIfNeeded = async () => {
      if (typeof window === 'undefined') return;
      if (typeof window.Capacitor?.isNativePlatform === 'function' && window.Capacitor.isNativePlatform()) {
        return;
      }

      const callbackData = parseAuthCallbackUrl(window.location.href);
      if (!callbackData?.isAuthCallback) return;

      const supabase = getSupabaseClient();

      if (callbackData.error) {
        throw new Error(callbackData.errorDescription || callbackData.error);
      }

      if (callbackData.code) {
        const { error } = await supabase.auth.exchangeCodeForSession(callbackData.code);
        if (error) throw error;

        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('code');
        cleanUrl.searchParams.delete('type');
        window.history.replaceState(window.history.state, '', cleanUrl.toString());
        return;
      }

      if (callbackData.accessToken && callbackData.refreshToken) {
        const { error } = await supabase.auth.setSession({
          access_token: callbackData.accessToken,
          refresh_token: callbackData.refreshToken,
        });
        if (error) throw error;

        const cleanUrl = new URL(window.location.href);
        cleanUrl.hash = '';
        window.history.replaceState(window.history.state, '', cleanUrl.toString());
      }
    };

    const hydrateAuthenticatedUser = async () => {
      try {
        const enrichedUser = await base44.auth.me();
        if (!isMounted || !enrichedUser) return;
        setUser(enrichedUser);
        setIsAuthenticated(true);
      } catch (_error) {
        // If the stored session is stale, clear it so protected actions stop using an invalid JWT.
        try {
          const supabase = getSupabaseClient();
          await supabase.auth.signOut();
        } catch (_signOutError) {
          // Ignore local cleanup failures; we still fall back to a logged-out UI state below.
        }

        if (!isMounted) return;
        setUser(null);
        setIsAuthenticated(false);
      }
    };

    const validateAndApplyStoredSession = async () => {
      try {
        const supabase = getSupabaseClient();
        const { data, error } = await supabase.auth.getUser();

        if (error || !data?.user) {
          await supabase.auth.signOut().catch(() => {});
          applyAuthUser(null);
          return;
        }

        applyAuthUser(data.user);
        hydrateAuthenticatedUser();
      } catch (_error) {
        if (!isMounted) return;
        setUser(null);
        setIsAuthenticated(false);
        setAuthError(null);
        setIsLoadingAuth(false);
      }
    };

    const bindAuthListener = async () => {
      try {
        const supabase = getSupabaseClient();
        await completeWebAuthCallbackIfNeeded();
        await validateAndApplyStoredSession();

        const { data } = supabase.auth.onAuthStateChange((event, session) => {
          if (event === 'SIGNED_OUT') {
            setUser(null);
            setIsAuthenticated(false);
            setAuthError(null);
            setIsLoadingAuth(false);
            return;
          }

          if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION' || event === 'PASSWORD_RECOVERY') {
            const authUser = session?.user || null;
            if (!authUser) {
              applyAuthUser(null);
              return;
            }

            applyAuthUser(authUser);
            void validateAndApplyStoredSession();
          }
        });
        subscription = data?.subscription;
      } catch (error) {
        console.error('Falha ao inicializar contexto de autenticacao:', error);
        if (isMounted) {
          setUser(null);
          setIsAuthenticated(false);
          if (error?.message) {
            toast.error(error.message);
            setAuthError({
              type: 'oauth_error',
              message: error.message,
            });
          } else {
            setAuthError(null);
          }
          setIsLoadingAuth(false);
        }
        subscription = null;
      }
    };

    bindAuthListener();
    if (typeof window !== 'undefined') {
      window.addEventListener('mbv-auth-callback', handleAuthCallbackEvent);
    }

    return () => {
      isMounted = false;
      subscription?.unsubscribe?.();
      if (typeof window !== 'undefined') {
        window.removeEventListener('mbv-auth-callback', handleAuthCallbackEvent);
      }
    };
  }, []);

  const checkAppState = async () => {
    try {
      setIsLoadingPublicSettings(true);
      setAuthError(null);
      setAppPublicSettings({
        id: 'local-app',
        public_settings: {
          auth_required: false
        }
      });
      setIsLoadingPublicSettings(false);
    } catch (error) {
      console.error('Unexpected error:', error);
      setAuthError({
        type: 'unknown',
        message: error.message || 'An unexpected error occurred'
      });
      setIsLoadingPublicSettings(false);
      setIsLoadingAuth(false);
    }
  };

  const logout = (shouldRedirect = true) => {
    setUser(null);
    setIsAuthenticated(false);
    
    if (shouldRedirect) {
      // Use the SDK's logout method which handles token cleanup and redirect
      base44.auth.logout(window.location.href);
    } else {
      // Just remove the token without redirect
      base44.auth.logout();
    }
  };

  const navigateToLogin = () => {
    base44.auth.redirectToLogin(window.location.href);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      isLoadingAuth,
      isLoadingPublicSettings,
      authError,
      appPublicSettings,
      logout,
      navigateToLogin,
      checkAppState
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
